import type { GlyphName } from "./glyphs";

/**
 * The trades a home-services business is organised by. A business names its
 * own business units ("HVAC Install", "Plumbing Service", "Drain Cleaning"),
 * so a trade is recognised from a name, most specific first: "Water Heater
 * Service" is water heaters before it is plumbing, "Heat Pump Install" is
 * HVAC before it is heating.
 */
export interface Trade {
  key: TradeKey;
  /** As a heading or a chip: "HVAC", "Water heaters". */
  label: string;
  glyph: GlyphName;
  patterns: readonly RegExp[];
}

export type TradeKey =
  | "hvac"
  | "heating"
  | "cooling"
  | "plumbing"
  | "drains"
  | "water_heater"
  | "electrical";

export const TRADES: readonly Trade[] = [
  {
    key: "water_heater",
    label: "Water heaters",
    glyph: "water_heater",
    patterns: [/water\s*heater/i, /tankless/i],
  },
  {
    key: "drains",
    label: "Drains",
    glyph: "drains",
    patterns: [/drain/i, /sewer/i, /septic/i, /jetting/i, /rooter/i],
  },
  {
    key: "hvac",
    label: "HVAC",
    glyph: "hvac",
    patterns: [
      /\bhvac\b/i,
      /heat\s*pump/i,
      /duct/i,
      /air\s*quality/i,
      /\biaq\b/i,
    ],
  },
  {
    key: "electrical",
    label: "Electrical",
    glyph: "electrical",
    patterns: [/electric/i, /generator/i, /\bpanel/i, /ev\s*charg/i, /wiring/i],
  },
  {
    key: "cooling",
    label: "Cooling",
    glyph: "cooling",
    patterns: [
      /\ba\/?c\b/i,
      /cooling/i,
      /air\s*condition/i,
      /mini[\s-]*split/i,
    ],
  },
  {
    key: "heating",
    label: "Heating",
    glyph: "heating",
    patterns: [/heating/i, /furnace/i, /boiler/i, /hydronic/i],
  },
  {
    key: "plumbing",
    label: "Plumbing",
    glyph: "plumbing",
    patterns: [/plumb/i, /pipe/i, /repipe/i, /leak/i, /fixture/i, /faucet/i],
  },
];

export interface Work {
  key: "install" | "service" | "maintenance" | "sales" | "warranty";
  label: string;
  patterns: readonly RegExp[];
}

export const WORK: readonly Work[] = [
  {
    key: "install",
    label: "Install",
    patterns: [/install/i, /replace/i, /new\s*construction/i],
  },
  {
    key: "maintenance",
    label: "Maintenance",
    patterns: [/maint/i, /tune[\s-]*up/i, /membership/i, /agreement/i],
  },
  { key: "warranty", label: "Warranty", patterns: [/warrant/i, /callback/i] },
  { key: "sales", label: "Sales", patterns: [/sales/i, /comfort\s*advisor/i] },
  {
    key: "service",
    label: "Service",
    patterns: [/service/i, /repair/i, /demand/i],
  },
];

const match = <T extends { patterns: readonly RegExp[] }>(
  items: readonly T[],
  name: string,
) => items.find((item) => item.patterns.some((pattern) => pattern.test(name)));

export const tradeOf = (name: string | null | undefined) =>
  name ? match(TRADES, name) : undefined;

export const workOf = (name: string | null | undefined) =>
  name ? match(WORK, name) : undefined;

/**
 * The records a report can name, as the report contract declares them
 * (`object.icon`). The script's own name for an object wins ("Installer",
 * "Crew"); these are what to say when it gave none.
 */
export type RecordKind =
  | "job"
  | "person"
  | "team"
  | "vendor"
  | "place"
  | "item";

export const RECORD_KINDS: Record<
  RecordKind,
  { noun: string; plural: string; glyph: GlyphName }
> = {
  job: { noun: "job", plural: "jobs", glyph: "job" },
  person: { noun: "technician", plural: "technicians", glyph: "technician" },
  team: { noun: "business unit", plural: "business units", glyph: "shop" },
  vendor: { noun: "vendor", plural: "vendors", glyph: "supplier" },
  place: { noun: "location", plural: "locations", glyph: "home" },
  item: { noun: "item", plural: "items", glyph: "part" },
};

/**
 * The glyph for a record: a business unit (or a job done in one) by its
 * trade when its name says which, else by what kind of record it is.
 */
export const glyphFor = (kind: RecordKind, trade?: string | null) => {
  if (kind === "team" || kind === "job") {
    const found = tradeOf(trade);
    if (found) return found.glyph;
  }
  return RECORD_KINDS[kind]?.glyph ?? "part";
};

/**
 * The words the product uses, and the ones it does not. Copy, column labels,
 * insight sentences and the report builder's instructions follow this list.
 */
export const VOCABULARY: readonly {
  use: string;
  not: readonly string[];
  why: string;
}[] = [
  {
    use: "job",
    not: ["work order", "ticket", "call"],
    why: "ServiceTitan's word, and the one owners say.",
  },
  {
    use: "technician",
    not: ["tech", "employee", "worker"],
    why: "Headings and labels. In a sentence a technician is their name.",
  },
  {
    use: "business unit",
    not: ["department", "division", "segment"],
    why: "ServiceTitan's word. Show the unit's own name wherever there is one.",
  },
  {
    use: "margin",
    not: ["GM%", "gross profit rate", "profitability"],
    why: "In sentences. A column keeps the business's own label.",
  },
  {
    use: "pts",
    not: ["%", "percent"],
    why: "A change in a margin is in points: 46.9% is +0.7 pts on 46.2%.",
  },
  {
    use: "short of target",
    not: ["shortfall", "loss", "leakage"],
    why: "What a job missed its target margin by, in dollars.",
  },
  {
    use: "flagged",
    not: ["anomaly", "issue detected", "alert"],
    why: "The report's rule caught it. It is not yet a problem.",
  },
  {
    use: "probably",
    not: ["AI says", "predicted", "100% confident"],
    why: "A guess is worded as one, never given as a figure.",
  },
  {
    use: "mark fixed · dismiss",
    not: ["resolve", "close", "ignore"],
    why: "What the reviewer did, in their words.",
  },
];
